// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { useLocale } from '../hooks/locale';
import type { lotPriceUnit_product$key } from './__generated__/lotPriceUnit_product.graphql';
import type { lotPrice_product$key } from './__generated__/lotPrice_product.graphql';
import type { lotPricePerMeterSquared_product$key } from './__generated__/lotPricePerMeterSquared_product.graphql';

type LotPriceUnitProps = {|
  product: lotPriceUnit_product$key,
|};

export const LotPriceUnit = (props: LotPriceUnitProps): React.Node => {
  const product = useFragment(
    graphql`
      fragment lotPriceUnit_product on Product {
        currency
        priceUnit
        offerType
        rentExtra
      }
    `,
    props.product,
  );
  const { t, numberLocale, currency } = useLocale();
  const {
    priceUnit,
    offerType,
    rentExtra,
    currency: productCurrency,
  } = product;

  const formatOptions = {
    style: 'currency',
    currency: productCurrency ?? currency,
    currencyDisplay: 'symbol',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  };

  let extra = '';
  if (rentExtra != null && rentExtra > 0) {
    extra = ` (+ ${numberLocale.format(rentExtra ?? 0, formatOptions)} ${t(
      'rentalExpenses',
    )})`;
  }
  if (offerType === 'rent') {
    switch (priceUnit) {
      case 'daily':
        return [t('perDay'), '', extra];
      case 'weekly':
        return [t('perWeek'), '', extra];
      case 'monthly':
        return [t('perMonth'), '', extra];
      case 'yearly':
        return [t('perYear'), '', extra];
      case 'm2yearly':
        return [t('perMeterSquaredPerYear'), '', extra];
      default:
    }
  } else if (offerType === 'sell') {
    return priceUnit === 'sellm2' ? t('perMeterSquared') : '';
  }
  return '';
};

type LotPriceProps = {|
  product: lotPrice_product$key,
|};

export const LotPrice = (props: LotPriceProps): React.Node => {
  const product = useFragment(
    graphql`
      fragment lotPrice_product on Product {
        currency
        offerType
        rentNet
        salePrice
      }
    `,
    props.product,
  );
  const { t, numberLocale, currency } = useLocale();
  const { offerType, rentNet, salePrice, currency: productCurrency } = product;

  const formatOptions = {
    style: 'currency',
    currency: productCurrency ?? currency,
    currencyDisplay: 'symbol',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  };

  if (offerType === 'rent' && rentNet != null) {
    return numberLocale.format(rentNet ?? 0, formatOptions);
  } else if (offerType === 'sell' && salePrice != null) {
    return numberLocale.format(salePrice ?? 0, formatOptions);
  }
  return t('priceOnRequest');
};

type LotPricePerMeterSquaredProps = {|
  product: lotPricePerMeterSquared_product$key,
|};

export const LotPricePerMeterSquared = (
  props: LotPricePerMeterSquaredProps,
): React.Node => {
  const product = useFragment(
    graphql`
      fragment lotPricePerMeterSquared_product on Product {
        currency
        salePrice
        property {
          livingSurface
          builtSurface
          weightedFloorSurface
          usableSurface
        }
      }
    `,
    props.product,
  );
  const { t, numberLocale, currency } = useLocale();
  const { salePrice, property, currency: productCurrency } = product;
  const { livingSurface, builtSurface, weightedFloorSurface, usableSurface } =
    property;

  const formatOptions = {
    style: 'currency',
    currency: productCurrency ?? currency,
    currencyDisplay: 'symbol',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  };

  let surface = null;
  let surfaceUnit = null;
  if (weightedFloorSurface != null) {
    surface = weightedFloorSurface;
    surfaceUnit = t('meterSquaredFloorSurface');
  } else if (usableSurface != null) {
    surface = usableSurface;
    surfaceUnit = t('meterSquaredUsableSurface');
  } else if (livingSurface != null) {
    surface = livingSurface;
    surfaceUnit = t('meterSquaredLivingSurface');
  } else if (builtSurface != null) {
    surface = builtSurface;
    surfaceUnit = t('meterSquaredBuiltSurface');
  }
  if (salePrice == null || salePrice === 0) {
    return null;
  }
  if (surface == null || surface === 0 || surfaceUnit == null) {
    return '-';
  }
  return `${numberLocale.format(
    salePrice / surface,
    formatOptions,
  )} / ${surfaceUnit}`;
};
